import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useDateFormat } from '@/context/DateFormatContext';
import { reportsAPI, bookingsAPI, propertiesAPI, settingsAPI } from '@/lib/api';
import { Building2, CalendarDays, TrendingUp, DollarSign, Share2, Clock, Loader2, Eye, Star, X, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, value, icon: Icon, color, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-2xl p-5 border border-gray-100 hover:border-airbnb-pink/20 hover:shadow-md transition-all cursor-pointer group"
    data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-airbnb-medium-gray">{title}</span>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color} group-hover:scale-105 transition-transform`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <p className="text-2xl sm:text-3xl font-bold text-airbnb-gray truncate" title={value}>{value}</p>
  </div>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const { formatDate } = useDateFormat();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [baseCurrency, setBaseCurrency] = useState('USD');
  
  // Testimonial state
  const [testimonialDialog, setTestimonialDialog] = useState(false);
  const [myTestimonial, setMyTestimonial] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({ rating: 5, content: '', role: '' });
  const [testimonialSubmitting, setTestimonialSubmitting] = useState(false);
  const [testimonialSuccess, setTestimonialSuccess] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [overviewResp, bookingsResp] = await Promise.all([
          reportsAPI.overview(),
          bookingsAPI.list()
        ]);
        setStats(overviewResp.data);
        setRecentBookings(bookingsResp.data.slice(0, 5));
        
        // Get currency from reports or settings
        if (overviewResp.data?.currency_symbol) {
          setCurrencySymbol(overviewResp.data.currency_symbol);
          setBaseCurrency(overviewResp.data.base_currency || 'USD');
        }
        
        // Check if user has submitted a testimonial
        try {
          const token = localStorage.getItem('ihostpms_token');
          const resp = await fetch(`${API_URL}/api/testimonials/mine`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (resp.ok) {
            const data = await resp.json();
            setMyTestimonial(data);
          }
        } catch {}
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSubmitTestimonial = async () => {
    if (!testimonialForm.content.trim()) return;
    
    setTestimonialSubmitting(true);
    try {
      const token = localStorage.getItem('ihostpms_token');
      const method = myTestimonial ? 'PUT' : 'POST';
      const url = myTestimonial ? `${API_URL}/api/testimonials/mine` : `${API_URL}/api/testimonials`;
      
      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(testimonialForm)
      });
      
      if (resp.ok) {
        setTestimonialSuccess(true);
        const data = await resp.json();
        if (!myTestimonial) {
          setMyTestimonial({ ...testimonialForm, status: 'pending' });
        } else {
          setMyTestimonial(prev => ({ ...prev, ...testimonialForm, status: 'pending' }));
        }
        setTimeout(() => {
          setTestimonialDialog(false);
          setTestimonialSuccess(false);
        }, 2000);
      }
    } catch (err) {
      console.error('Testimonial submit error:', err);
    } finally {
      setTestimonialSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-airbnb-pink animate-spin" />
      </div>
    );
  }

  const statusColors = {
    confirmed: 'bg-emerald-50 text-emerald-700',
    pending: 'bg-amber-50 text-amber-700',
    'checked-in': 'bg-blue-50 text-blue-700',
    'checked-out': 'bg-gray-100 text-gray-600',
    cancelled: 'bg-red-50 text-red-600',
  };

  const channelColors = {
    direct: 'bg-gray-100 text-gray-700',
    airbnb: 'bg-pink-50 text-airbnb-pink',
    'booking.com': 'bg-blue-50 text-blue-700',
    agoda: 'bg-purple-50 text-purple-700',
    'hotels.com': 'bg-red-50 text-red-700',
    expedia: 'bg-yellow-50 text-yellow-800',
  };

  return (
    <div data-testid="dashboard-page" className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-airbnb-gray">Dashboard</h1>
        <p className="text-airbnb-medium-gray mt-1">Welcome back, {user?.name || 'Host'}!</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <StatCard title="Properties" value={stats?.total_properties || 0} icon={Building2} color="bg-airbnb-light-pink text-airbnb-pink" onClick={() => navigate('/properties')} />
        <StatCard title="Bookings" value={stats?.total_bookings || 0} icon={CalendarDays} color="bg-blue-50 text-blue-600" onClick={() => navigate('/bookings')} />
        <StatCard title="Occupancy" value={`${stats?.occupancy_rate || 0}%`} icon={TrendingUp} color="bg-emerald-50 text-emerald-600" onClick={() => navigate('/calendar')} />
        <StatCard title="Revenue" value={`${currencySymbol}${(stats?.total_revenue || 0).toLocaleString()}`} icon={DollarSign} color="bg-amber-50 text-amber-600" onClick={() => navigate('/reports')} />
        <StatCard title="Channels" value={stats?.active_channels || 0} icon={Share2} color="bg-purple-50 text-purple-600" onClick={() => navigate('/channels')} />
        <StatCard title="Avg Stay" value={`${stats?.avg_stay_length || 0}d`} icon={Clock} color="bg-teal-50 text-teal-600" onClick={() => navigate('/reports')} />
      </div>

      {/* Submit Testimonial Card */}
      <div className="mb-8 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-5 border border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Star className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white">
                {myTestimonial ? 'Your Testimonial' : 'Share Your Experience'}
              </h3>
              <p className="text-slate-400 text-sm">
                {myTestimonial 
                  ? `Status: ${myTestimonial.status === 'featured' ? 'Featured on homepage' : myTestimonial.status === 'approved' ? 'Published' : 'Pending review'}`
                  : 'Help other hosts by sharing your iHostPMS experience'
                }
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              if (myTestimonial) {
                setTestimonialForm({
                  rating: myTestimonial.rating || 5,
                  content: myTestimonial.content || '',
                  role: myTestimonial.role || ''
                });
              }
              setTestimonialDialog(true);
            }}
            className={myTestimonial ? 'bg-slate-700 hover:bg-slate-600' : 'bg-amber-500 hover:bg-amber-600'}
            data-testid="submit-testimonial-btn"
          >
            <Star className="w-4 h-4 mr-2" />
            {myTestimonial ? 'Edit Testimonial' : 'Write Testimonial'}
          </Button>
        </div>
      </div>

      {/* Testimonial Dialog */}
      <Dialog open={testimonialDialog} onOpenChange={setTestimonialDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              {myTestimonial ? 'Edit Your Testimonial' : 'Share Your Experience'}
            </DialogTitle>
            <DialogDescription>
              Tell other property hosts about your experience with iHostPMS
            </DialogDescription>
          </DialogHeader>
          
          {testimonialSuccess ? (
            <div className="py-8 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800">Thank You!</h3>
              <p className="text-slate-500">Your testimonial has been submitted for review.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Star Rating */}
              <div>
                <Label className="text-sm font-medium">Your Rating</Label>
                <div className="flex items-center gap-1 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setTestimonialForm(f => ({ ...f, rating: star }))}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-8 h-8 ${star <= testimonialForm.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Role */}
              <div>
                <Label className="text-sm font-medium">Your Role (optional)</Label>
                <Input
                  value={testimonialForm.role}
                  onChange={(e) => setTestimonialForm(f => ({ ...f, role: e.target.value }))}
                  placeholder="e.g., Property Manager, Airbnb Host"
                  className="mt-1"
                />
              </div>

              {/* Content */}
              <div>
                <Label className="text-sm font-medium">Your Testimonial *</Label>
                <Textarea
                  value={testimonialForm.content}
                  onChange={(e) => setTestimonialForm(f => ({ ...f, content: e.target.value }))}
                  placeholder="Share your experience with iHostPMS. What do you love most? How has it helped your business?"
                  rows={5}
                  className="mt-1"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setTestimonialDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitTestimonial}
                  disabled={!testimonialForm.content.trim() || testimonialSubmitting}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  {testimonialSubmitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><Star className="w-4 h-4 mr-2" /> {myTestimonial ? 'Update' : 'Submit'}</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recent Bookings */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
          <h2 className="text-lg font-bold text-airbnb-gray">Recent Bookings</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/bookings')} className="text-airbnb-pink hover:bg-airbnb-light-pink" data-testid="view-all-bookings">
            <Eye className="w-4 h-4 mr-1" /> View All
          </Button>
        </div>

        {recentBookings.length === 0 ? (
          <div className="text-center py-12">
            <CalendarDays className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-airbnb-medium-gray">No bookings yet</p>
            <Button onClick={() => navigate('/bookings')} className="mt-3 bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl" size="sm">
              Create Booking
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="recent-bookings-table">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-airbnb-medium-gray uppercase tracking-wider">Guest</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-airbnb-medium-gray uppercase tracking-wider">Property</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-airbnb-medium-gray uppercase tracking-wider">Check-in</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-airbnb-medium-gray uppercase tracking-wider">Check-out</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-airbnb-medium-gray uppercase tracking-wider">Channel</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-airbnb-medium-gray uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-airbnb-medium-gray uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recentBookings.map((b) => (
                  <tr key={b.booking_id} className="hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => navigate('/bookings')}>
                    <td className="px-6 py-3.5">
                      <span className="font-medium text-airbnb-gray text-sm">{b.guest_name}</span>
                    </td>
                    <td className="px-6 py-3.5 text-sm text-airbnb-medium-gray">{b.property_name}</td>
                    <td className="px-6 py-3.5 text-sm text-airbnb-medium-gray">{formatDate(b.check_in)}</td>
                    <td className="px-6 py-3.5 text-sm text-airbnb-medium-gray">{formatDate(b.check_out)}</td>
                    <td className="px-6 py-3.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${channelColors[b.channel] || 'bg-gray-100 text-gray-700'}`}>
                        {b.channel}
                      </span>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[b.status] || 'bg-gray-100 text-gray-600'}`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-right text-sm font-semibold text-airbnb-gray">{currencySymbol}{b.total_price?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
